import type { RootStackParamList } from '../../../../../../navigation/types';
import type { InferenceMethod } from '../../../requirements/TaxonomyEntity';

import { getFooterButtons } from './CollectionModal.util';
import { MinimumPayForm, type SalaryPreference } from './forms/MinimumPayForm';
import { SentimentForm } from './forms/SentimentForm';
import { styles } from './styles';
import { Box, Button, ScrollView } from '../../../../../../../../mads';
import { Text } from '../../../../../../../../mads/Text';
import { useI18n } from '../../../../../../../../shared/I18n';
import Sentiment from '../../../../../../../../shared/util/salary/Sentiment';
import { useModalContext } from '../../../context/JMUIModalContextProvider';
import { MarkupWrapper } from '../MarkupWrapper';
import { QuestionText } from '../QuestionText';
import { stripMarkup } from '../utils/stripMarkup';

import { type RouteProp, useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type DataT = {
    sentiment: Sentiment;
    salaryPreference?: SalaryPreference;
};

export type CollectionModalProps = {
    isOpen?: boolean;
    onModalReset: () => Promise<boolean>;
    onClose?: () => void;

    title: string;
    question: string;
    editDisclaimer?: string;

    inferenceMethod?: InferenceMethod;
    showSalary?: boolean;

    data: DataT;
    onModalSubmit: (data: DataT) => Promise<boolean>;
    setData?: (d: Partial<DataT>) => void;
};

export type CollectionModalScreenRouteProps = {
    route: RouteProp<RootStackParamList, 'CollectionModal'>;
};

export function CollectionModal({ route }: CollectionModalScreenRouteProps) {
    const { question, editDisclaimer, inferenceMethod, showSalary, data } = route.params;

    const navigation = useNavigation();
    const { hasSubmitted, setHasSubmitted, collectionModalCallbacksRef } = useModalContext();
    const gt = useI18n();
    const [loading, setLoading] = useState(false);
    const [modalData, setModalData] = useState(data);
    const [initialData, setInitialData] = useState(data);
    const [footerHeight, setFooterHeight] = useState(0);
    const { bottom } = useSafeAreaInsets();

    useEffect(() => {
        setModalData(data);
        setInitialData(data);
    }, [data]);

    // Restore focus to the tile that opened this modal when it closes
    useEffect(() => {
        return () => {
            // Access ref.current at cleanup time to get the latest callback, not at effect setup time
            // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally access ref.current at cleanup time
            collectionModalCallbacksRef.current.onCleanup?.();
        };
    }, [collectionModalCallbacksRef]);

    // Get callbacks from context ref
    const callbacks = collectionModalCallbacksRef.current;

    const { onModalSubmit, onModalReset, onClose } = callbacks;

    const handleSave = async (sentiment: Sentiment) => {
        let saveStatus = false;

        setHasSubmitted(true);
        setLoading(true);

        saveStatus = await onModalSubmit({ ...modalData, sentiment });
        if (saveStatus) {
            setModalData({ ...modalData, sentiment });
        }

        setLoading(false);

        return saveStatus;
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }

        navigation.goBack();
    };

    const originalSentiment = initialData?.sentiment || Sentiment.UNKNOWN;
    const sentiment = modalData?.sentiment || Sentiment.UNKNOWN;
    const footerButtons = getFooterButtons({
        sentiment,
        inferenceMethod,
        originalSentiment,
        loading,
        gt,
        onModalReset: async () => {
            const resetStatus = await onModalReset();
            setHasSubmitted(false);
            if (resetStatus) {
                handleClose();
            }
            return resetStatus;
        },
        handleSave,
        showSalary,
        onClose: handleClose,
    });

    return (
        <Box sx={styles.modalContainer}>
            <ScrollView
                sx={[styles.modalContent, { marginBottom: footerHeight + bottom }]}
                contentContainerSx={styles.scrollContent}
            >
                <Box sx={{ marginBottom: 4 }}>
                    <Text level={4} variant="contrast" data-testid="modal-question">
                        <QuestionText>{question}</QuestionText>
                    </Text>
                    {editDisclaimer && originalSentiment === Sentiment.POSITIVE && (
                        <Text level={2} variant="secondary">
                            <MarkupWrapper label={editDisclaimer} sx={{ fontWeight: 'bold' }} />
                        </Text>
                    )}
                </Box>
                {showSalary ? (
                    <MinimumPayForm
                        value={modalData.salaryPreference}
                        onChange={(s) => {
                            setModalData({ ...modalData, salaryPreference: s });
                        }}
                        showErrors={hasSubmitted}
                    />
                ) : (
                    <SentimentForm
                        isLoading={loading}
                        onChange={(d) => {
                            setModalData({ ...modalData, ...d });
                        }}
                        value={modalData}
                        handleSave={handleSave}
                        onClose={handleClose}
                        accessibilityQuestionText={stripMarkup(question)}
                    />
                )}
            </ScrollView>
            <Box
                sx={[styles.footerButtons, { bottom: bottom || 0 }]}
                onLayout={(event) => {
                    const { height } = event.nativeEvent.layout;
                    setFooterHeight(height);
                }}
            >
                {footerButtons?.map((buttonProps, index) => {
                    return (
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        <Button key={index} {...buttonProps.props} sx={{ width: '100%' }}>
                            {}
                            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                            {buttonProps.label}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
}

export default CollectionModal;
